import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

import {AUDIT_LOG_EMPTY_CELL_VALUE} from 'audit/AuditConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

import parsePythonReprToJson from './parsePythonReprToJson';

interface JsonFormattedCellValueProps {
  value: string | undefined;
}

function looksLikeStructuredData(value: string): boolean {
  const firstChar = value.trimStart()[0];
  return firstChar === '{' || firstChar === '[';
}

/**
 * If value is a JSON or Python repr object/array string, returns it pretty-printed
 * with 2-space indentation. Tries JSON.parse first, then falls back to Python repr
 * normalization (handles None, True, False, single quotes).
 * Otherwise returns value unchanged.
 */
function tryFormatJson(value: string): {formatted: string; isJson: boolean} {
  if (!looksLikeStructuredData(value)) {
    return {formatted: value, isJson: false};
  }

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed != null) {
      return {formatted: JSON.stringify(parsed, null, 2), isJson: true};
    }
  } catch (e: unknown) {
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }

  const parsed = parsePythonReprToJson(value);
  if (parsed != null) {
    return {formatted: JSON.stringify(parsed, null, 2), isJson: true};
  }

  return {formatted: value, isJson: false};
}

export default function JsonFormattedCellValue({value}: JsonFormattedCellValueProps): JSX.Element {
  const {classes} = useStyles();
  const {formatted, isJson} = useMemo(
    () => (value ? tryFormatJson(value) : {formatted: value, isJson: false}),
    [value]
  );

  if (isJson) {
    return (
      <div {...getTestProps({component: 'JsonFormattedCellValue', element: 'root'})}>
        <pre
          className={classes.jsonPre}
          {...getTestProps({component: 'JsonFormattedCellValue', element: 'pre'})}
        >
          {formatted}
        </pre>
      </div>
    );
  }

  return (
    <div {...getTestProps({component: 'JsonFormattedCellValue', element: 'root'})}>
      {formatted || AUDIT_LOG_EMPTY_CELL_VALUE}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  jsonPre: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere' as const,
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.md,
    maxHeight: 200,
    overflow: 'auto',
    backgroundColor: theme.colors.gray[0],
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    margin: 0,
    lineHeight: 1.45,
  },
}));
